// src/components/main/AllCompetitionsSection.jsx

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import CompetitionCard from "@/components/competitions/CompetitionCard";
import { api } from "@/services/apiService";

const getStatus = (comp) => {
  const now = Date.now();
  if (comp.end_date && now > new Date(comp.end_date).getTime()) return "ended";
  if (comp.start_date && now < new Date(comp.start_date).getTime()) return "upcoming";
  return "live";
};

const STATUS_ORDER = { upcoming: 0, live: 1, ended: 2 };

const AllCompetitionsSection = () => {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading]           = useState(false);
  const [search, setSearch]             = useState("");
  const [order, setOrder]               = useState("latest");

  const fetchCompetitions = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/api/competitions/?search=${encodeURIComponent(search)}&order=${order}`
      );
      const data = Array.isArray(res.data) ? res.data : [];
      const sorted = [...data].sort((a, b) =>
        STATUS_ORDER[getStatus(a)] - STATUS_ORDER[getStatus(b)]
      );
      setCompetitions(sorted);
    } catch (err) {
      console.error("Failed to fetch competitions:", err);
      setCompetitions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCompetitions(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => { fetchCompetitions(); }, 300);
    return () => clearTimeout(timer);
  }, [search, order]);

  return (
    <section className="w-full bg-stone-brown py-6 max-w-[95rem] mx-auto">

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-8 py-2 px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-mikado-yellow">
            All Competitions
          </h2>
          <div className="flex items-center gap-3">
            <Input
              type="text"
              placeholder="Search..."
              className="w-full sm:max-w-xs bg-pebble-brown border-none text-white placeholder-swan"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              variant="secondary"
              className="bg-pebble-brown text-white hover:bg-salmon-pearl text-sm shrink-0"
              onClick={() => setOrder(order === "latest" ? "oldest" : "latest")}
            >
              {order === "latest" ? "Latest" : "Oldest"}
            </Button>
          </div>
        </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-swan" />
        </div>
      ) : competitions.length === 0 ? (
        <p className="text-swan text-center mt-20">No competitions found.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-4 place-items-stretch px-4">
          {competitions.map((comp) => (
            <CompetitionCard key={comp.id} competition={comp} />
          ))}
        </div>
      )}

    </section>
  );
};

export default AllCompetitionsSection;